"use client";

import { useEffect, useRef, useState } from "react";
import type { ElementType, ReactNode } from "react";
import { cn } from "@/utils";

type RevealProps = {
  children: ReactNode;
  className?: string;
  /** stagger delay in ms */
  delay?: number;
  as?: ElementType;
  /** anchor target, e.g. for in-page nav links */
  id?: string;
};

const Reveal = ({ children, className, delay = 0, as: Tag = "div", id }: RevealProps) => {
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setShown(true);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.1 }
    );
    io.observe(el);

    // Safety net — never leave content hidden if the observer never fires.
    const fallback = setTimeout(() => setShown(true), 2400);

    return () => {
      io.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <Tag
      ref={ref}
      id={id}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      className={cn(
        "jlt-transition-[opacity,transform] jlt-duration-700 jlt-ease-spring motion-reduce:jlt-transition-none",
        shown ? "jlt-opacity-100 jlt-translate-y-0" : "jlt-opacity-0 jlt-translate-y-[30px]",
        className
      )}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
